import { Component, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { tap, finalize, catchError } from 'rxjs/operators';
import { Plan} from 'src/app/model/Plan';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { EventService } from 'src/app/service/event.service';
import { PlanService } from 'src/app/service/plan.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { ModalConfig } from '../controls/modal/modal-config';
import { Modal } from '../controls/modal/modal.service';
import { PlansFormMode } from '../plans-form/plans-form.component';

@Component({
    selector: 'app-plans-import',
    templateUrl: './plans-import.component.html',
    styleUrls: ['./plans-import.component.css', '../plans/plans.component.css'],
    standalone: false
})
export class PlansImportComponent implements OnInit {
  mode:PlansFormMode = PlansFormMode.IMPORT;
  @Output() newPlan:Plan;
  formGroup: FormGroup;
  solutionInfo : SolutionInfo = new SolutionInfo();
  deactivate:boolean = false;
  isImporting:boolean = false;

  //subscription
  solutionInfoSubscription: Subscription;
  
  constructor(private eventService: EventService, private router: Router, 
              private planSrv: PlanService, private modal: Modal) {
   }

  ngOnInit(): void {
    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.setNewPlan();
    });
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
  }

  updateForm(formGroup:FormGroup): void {
    setTimeout(() => {
      this.formGroup = formGroup;
      this.newPlan.name = this.formGroup.get('nameCtrl').value;
      this.newPlan.description = this.formGroup.get('descriptionCtrl').value;
    });
  }

  canDeactivate() : boolean{
    return !this.formGroup.dirty || this.deactivate;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-plans-import-unsaved-changes');
  }
  
  importPlan():void {
    this.isImporting = true;
    setTimeout(() => {
      this.planSrv.importPlan(
        this.newPlan, this.formGroup.value.planFileSourceCtrl.fileSource
      ).pipe(
              tap(resp => {
                this.newPlan.id = resp.body.id;
                this.deactivate = true;
  
                if(this.newPlan.id > 0){
                  const sub: Subscription =
                    this.modal.open(ModalMessageComponent, {
                      data: {
                        title: $localize`Edit plan`,
                        message: $localize`The selected plan was imported successfully. <br/> Would you like to edit the plan?`,
                        primaryBtn: $localize`Yes`,
                        secondaryBtn: $localize`No`,
                        type: ModalMessageType.Question,
                        width: '450px'
                      },
                      id: 'otsc-plans-import-and-edit-msg'
                    }
                    ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
                      if (isPrimaryBtnClicked) {
                        //go to details
                        this.router.navigate(['/plans/details/' + this.newPlan.id]);
                      } else {
                        //go to overview
                        this.router.navigate(['/plans/overview']);
                      }
                      sub.unsubscribe();
                    });                
                }else{
                  Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Error importing the plan. Please make sure the file is valid before trying again.`);
                }
              }),
              catchError(val => {
                Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Error importing the plan. Please make sure the file is valid before trying again.`);
                return of(val);
              }),
              finalize(() =>{
                this.isImporting = false;
              })
      ).subscribe();
    }, 500);
  }

  setNewPlan(): void {
    if(this.newPlan === undefined){
      this.newPlan = new Plan();
    }

    this.newPlan.selectedEnvironmentName = this.solutionInfo.currentEnvironment.name;
    this.newPlan.selectedEnvironmentId = this.solutionInfo.currentEnvironment.id;
    this.newPlan.name = this.newPlan.name != '' ? this.newPlan.name : '';
    this.newPlan.description = this.newPlan.description != '' ? this.newPlan.description : '';

    if(this.formGroup){
      this.formGroup.get('nameCtrl').setValue(this.newPlan.name);
      this.formGroup.get('descriptionCtrl').setValue(this.newPlan.description);
      this.formGroup.get('envCtrl').setValue(this.solutionInfo.currentEnvironment.name);
    }
  }

  cancel(): void {
    this.router.navigate(['/plans/overview']);
  }
}
